import { createSelector } from 'reselect'

const selectPopupAudience = (state) => state.settings.deepfakeGeneral.self.popupAudience
const selectSendWarningReportNotification = (state) =>
  state.settings.deepfakeGeneral.self.sendWarningReportNotification
const selectLiveDeepfakeAlert = (state) => state.settings.deepfakeGeneral.self.liveDeepfakeAlert
const selectSwitchersLoader = (state) => state.settings.deepfakeGeneral.self.switchersLoader
const selectWarningReportRecipients = (state) =>
  state.settings.deepfakeGeneral.self.warningReportRecipients
const selectSelectedRecipientId = (state) => state.settings.deepfakeGeneral.self.selectedRecipientId
const selectCandidatesLoader = (state) => state.settings.deepfakeGeneral.self.candidatesLoader
const selectCandidates = (state) => state.settings.deepfakeGeneral.self.candidates
const selectWarningReportRecipientsLoader = (state) =>
  state.settings.deepfakeGeneral.self.warningReportRecipientsLoader

// Consent management selectors
const selectConsentText = (state) => state.settings.deepfakeGeneral.self.consentText
const selectConsentLink = (state) => state.settings.deepfakeGeneral.self.consentLink
const selectConsentTimeoutAmountInSeconds = (state) =>
  state.settings.deepfakeGeneral.self.consentTimeoutAmountInSeconds
const selectConsentType = (state) => state.settings.deepfakeGeneral.self.consentType
const selectConsentData = (state) => state.settings.deepfakeGeneral.self.consentData
const selectJoinExternalMeetings = (state) =>
  state.settings.deepfakeGeneral.self.joinExternalMeetings
const selectEmailAlertsForInternalsInExternalMeetings = (state) =>
  state.settings.deepfakeGeneral.self.emailAlertsForInternalsInExternalMeetings

// MFA Challenge selectors
const selectMfaEnabled = (state) => state.settings.deepfakeGeneral.self.mfaEnabled
const selectMfaLinkTimeoutSeconds = (state) =>
  state.settings.deepfakeGeneral.self.mfaLinkTimeoutSeconds
const selectMfaGlobalTimerSeconds = (state) =>
  state.settings.deepfakeGeneral.self.mfaGlobalTimerSeconds
const selectMfaSuspicionAlertEnabled = (state) =>
  state.settings.deepfakeGeneral.self.mfaSuspicionAlertEnabled
const selectMfaFailureAlertEnabled = (state) =>
  state.settings.deepfakeGeneral.self.mfaFailureAlertEnabled

// SIEM events selector
const selectSiemEventsEnabled = (state) => state.settings.deepfakeGeneral.self.siemEventsEnabled

// Wizard config selectors
const selectWizardConfigData = (state) =>
  state.settings.deepfakeGeneral.self.wizardConfig || { data: null, loader: false, error: null }

export const selectWizardConfig = createSelector(
  selectWizardConfigData,
  (wizardConfigData) => wizardConfigData.data
)

export const selectWizardConfigLoader = createSelector(
  selectWizardConfigData,
  (wizardConfigData) => wizardConfigData.loader
)

export const selectWizardConfigError = createSelector(
  selectWizardConfigData,
  (wizardConfigData) => wizardConfigData.error
)

export const selectEulaSignedAt = createSelector(
  selectWizardConfig,
  (config) => config?.eulaSignedAt
)

export const selectIsEulaSigned = createSelector(
  selectWizardConfig,
  (config) => config?.isEulaSigned || false
)

export const selectEulaPdfUrl = createSelector(selectWizardConfig, (config) => config?.eulaPdfUrl)

export const selectMicrosoftConsentUrl = createSelector(
  selectWizardConfig,
  (config) => config?.microsoftConsentUrl
)

export const selectInstallerPaths = createSelector(
  selectWizardConfig,
  (config) => config?.installerPaths
)

export const selectEulaVersion = createSelector(selectWizardConfig, (config) => config?.eulaVersion)

// Verify consent selectors
const selectVerifyConsentData = (state) => state.settings.deepfakeGeneral.self.verifyConsent

export const selectVerifyConsent = createSelector(
  selectVerifyConsentData,
  (verifyConsentData) => verifyConsentData.data
)

export const selectVerifyConsentLoader = createSelector(
  selectVerifyConsentData,
  (verifyConsentData) => verifyConsentData.loader
)

export const selectVerifyConsentError = createSelector(
  selectVerifyConsentData,
  (verifyConsentData) => verifyConsentData.error
)

// Graph API permissions selectors
export const selectPermissionsGranted = (state) =>
  state.settings.deepfakeGeneral.self.permissionsGranted

export const selectConnectionStatus = createSelector(
  [selectVerifyConsent, selectVerifyConsentLoader, selectVerifyConsentError],
  (verifyConsentData, loader, error) => {
    if (loader) return 'loading'
    if (error || verifyConsentData?.consentVerified === false) return 'error'
    if (verifyConsentData?.consentVerified) return 'success'
    return null
  }
)

const selectCandidatesOptions = createSelector(selectCandidates, (candidates) =>
  candidates.map(({ userId, recipientFirstName, recipientLastName }) => ({
    label: `${recipientFirstName} ${recipientLastName}`,
    value: userId,
  }))
)

export {
  selectPopupAudience,
  selectSendWarningReportNotification,
  selectLiveDeepfakeAlert,
  selectSwitchersLoader,
  selectWarningReportRecipients,
  selectCandidatesLoader,
  selectCandidates,
  selectCandidatesOptions,
  selectSelectedRecipientId,
  selectWarningReportRecipientsLoader,
  // Consent management selectors
  selectConsentText,
  selectConsentLink,
  selectConsentTimeoutAmountInSeconds,
  selectConsentType,
  selectConsentData,
  selectJoinExternalMeetings,
  selectEmailAlertsForInternalsInExternalMeetings,
  // MFA Challenge selectors
  selectMfaEnabled,
  selectMfaLinkTimeoutSeconds,
  selectMfaGlobalTimerSeconds,
  selectMfaSuspicionAlertEnabled,
  selectMfaFailureAlertEnabled,
  // SIEM events selector
  selectSiemEventsEnabled,
}
